import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  useTheme,
  useMediaQuery,
  CardActions,
  Stack,
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { takePendingClaimToken } from '../utils/authRedirect';
import api from '../services/httpClient';
import { trackGatewayRegistration } from '../analytics';
import { useAuth } from '../context/AuthContext';
import { connectToGateway, reportGatewayAccessFailure } from '../utils/gatewayAccess';
import SubscriptionPromptDialog from './SubscriptionPromptDialog';
import {
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  Router as RouterIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  QrCode as QrCodeIcon,
  Launch as LaunchIcon,
  PersonAdd as PersonAddIcon,
  CreditCard as SubscriptionIcon,
} from '@mui/icons-material';

// Services reachable through /proxy/gateway/{id}/<path> on the gateway's
// VPN-facing nginx (see docs/hub-gateway-add-service/).
const GATEWAY_SERVICES = [
  {
    key: 'portainer',
    label: 'Open Portainer',
    path: '/portainer/',
    adminOnly: true,
  },
];

const Gateways = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isAdmin = user?.isGatewayOwner === true;
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'add', 'edit', 'delete', 'vpn', 'claim'
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuGateway, setMenuGateway] = useState(null);
  const [claimToken, setClaimToken] = useState('');
  const [searchParams] = useSearchParams();
  const [showAllGateways, setShowAllGateways] = useState(false);
  const [subscriptionPromptOpen, setSubscriptionPromptOpen] = useState(false);
  const [blockedGateway, setBlockedGateway] = useState(null);
  const [editGatewayName, setEditGatewayName] = useState('');
  const [editGatewayLocation, setEditGatewayLocation] = useState('');
  
  // Calculate subscription info from gateways
  const subscriptionInfo = React.useMemo(() => {
    if (!gateways || gateways.length === 0) return null;
    
    const myGateways = gateways.filter(g => g.owner?.username === user?.username);
    if (myGateways.length === 0) return null;
    
    const gateway = myGateways[0];
    if (!gateway.subscriptionStatus) return null;
    
    return {
      status: gateway.subscriptionStatus,
      hasSubscription: gateway.owner?.subscription?.status === 'ACTIVE',
      inTrial: gateway.subscriptionStatus.includes('Trial'),
      expired: gateway.subscriptionStatus.includes('Required') || gateway.subscriptionStatus.includes('Expired'),
    };
  }, [gateways, user]);

  useEffect(() => {
    fetchGateways();
    const interval = setInterval(fetchGateways, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [showAllGateways]);

  // Check if token is in URL query parameter (or remembered through login) and auto-open claim dialog
  useEffect(() => {
    const tokenParam = searchParams.get('token') || takePendingClaimToken();
    if (tokenParam) {
      setClaimToken(tokenParam);
      setDialogType('claim');
      setDialogOpen(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  const fetchGateways = async () => {
    try {
      const response = await api.get('/v1/hub/gateways', {
        params: {
          showAll: showAllGateways,
        },
      });
      setGateways(response.data);
    } catch (error) {
      console.error('Failed to fetch gateways:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGatewaysWithFilter = async (showAll) => {
    try {
      const response = await api.get('/v1/hub/gateways', {
        params: {
          showAll: showAll,
        },
      });
      setGateways(response.data);
    } catch (error) {
      console.error('Failed to fetch gateways:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, gateway) => {
    setAnchorEl(event.currentTarget);
    setMenuGateway(gateway);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuGateway(null);
  };

  const handleDialogOpen = (type, gateway = null) => {
    setDialogType(type);
    setSelectedGateway(gateway);
    
    // Populate edit fields if editing
    if (type === 'edit' && gateway) {
      setEditGatewayName(gateway.name || '');
      setEditGatewayLocation(gateway.location || '');
    }
    
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedGateway(null);
    setDialogType('');
    setClaimToken('');
  };

  const handleClaimGateway = async () => {
    try {
      // Check if user has ACTIVE subscription and warn about price increase
      const myGateways = gateways.filter(g => g.owner?.username === user?.username);
      const hasActiveSubscription = myGateways.length > 0 && 
                                    myGateways[0].owner?.subscription?.status === 'ACTIVE';
      
      if (hasActiveSubscription) {
        const currentGatewayCount = myGateways.length;
        const currentCost = currentGatewayCount * 15;
        const newGatewayCount = currentGatewayCount + 1;
        const newCost = newGatewayCount * 15;
        const increase = newCost - currentCost;
        
        const confirmed = window.confirm(
          `⚠️ Subscription Price Increase\n\n` +
          `Claiming this gateway will increase your monthly subscription cost:\n\n` +
          `Current: $${currentCost}/month (${currentGatewayCount} gateway${currentGatewayCount !== 1 ? 's' : ''})\n` +
          `New: $${newCost}/month (${newGatewayCount} gateways)\n` +
          `Increase: +$${increase}/month\n\n` +
          `A prorated charge will be applied to your card immediately.\n\n` +
          `Do you want to continue?`
        );
        
        if (!confirmed) {
          return; // User cancelled
        }
      }
      
      await api.post('/v1/gateway/claim', {
        deviceToken: claimToken
      });
      trackGatewayRegistration();

      // Refresh the gateways list to show the newly claimed gateway
      fetchGateways();
      handleDialogClose();
    } catch (error) {
      console.error('Failed to claim gateway:', error);
      // Check if it's a 401 authentication error
      if (error.response?.status === 401) {
        console.log('Authentication failed, user might be logged out');
        // You might want to redirect to login or refresh the token here
      }
      // Show error message to user
      alert(error.response?.data?.error || `Failed to claim gateway: ${error.message}`);
    }
  };

  const handleAdminClaimGateway = async (gateway) => {
    try {
      // Use the gateway's device token to claim it as admin
      await api.post('/v1/gateway/claim', {
        deviceToken: gateway.deviceToken
      });
      trackGatewayRegistration();

      // Refresh the gateways list
      fetchGatewaysWithFilter(showAllGateways);
      handleDialogClose();
    } catch (error) {
      console.error('Failed to claim gateway as admin:', error);
      alert(error.response?.data?.error || `Failed to claim gateway: ${error.message}`);
    }
  };

  const handleDeleteGateway = async () => {
    try {
      await api.delete(`/v1/hub/gateways/${selectedGateway.id}`);
      
      // Refresh the gateways list
      fetchGatewaysWithFilter(showAllGateways);
      handleDialogClose();
    } catch (error) {
      console.error('Failed to delete gateway:', error);
      alert(`Failed to delete gateway: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleEditGateway = async () => {
    try {
      await api.put(`/v1/hub/gateways/${selectedGateway.id}`, {
        deviceName: editGatewayName,
        deviceLocation: editGatewayLocation
      });
      
      // Refresh the gateways list
      fetchGatewaysWithFilter(showAllGateways);
      handleDialogClose();
    } catch (error) {
      console.error('Failed to update gateway:', error);
      alert(`Failed to update gateway: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleGatewayAccess = async (gateway, servicePath = '') => {
    handleMenuClose();
    const result = await connectToGateway(gateway, servicePath);
    if (result.ok) {
      return;
    }
    if (result.reason === 'subscription_required') {
      setBlockedGateway(gateway);
      setSubscriptionPromptOpen(true);
      return;
    }
    reportGatewayAccessFailure(result);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon color="success" />;
      case 'offline':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <ErrorIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getSubscriptionStatus = (gateway) => {
    // Check if gateway has subscription info from backend
    if (gateway.subscriptionStatus) {
      return gateway.subscriptionStatus;
    }
    
    // Fallback: compute from trial fields if available
    if (gateway.trialPeriodStart && !gateway.subscriptionRequired) {
      const trialStart = new Date(gateway.trialPeriodStart);
      const now = new Date();
      const daysSinceStart = Math.floor((now - trialStart) / (1000 * 60 * 60 * 24));
      const daysRemaining = 30 - daysSinceStart;
      
      if (daysRemaining > 0) {
        return `Trial: ${daysRemaining}d left`;
      }
    }
    
    if (gateway.subscriptionRequired) {
      return 'Subscription Required';
    }
    
    if (!gateway.owner) {
      return 'Unclaimed';
    }
    
    return 'Unknown';
  };

  const getSubscriptionStatusColor = (statusText) => {
    if (statusText.includes('Active')) return 'success';
    if (statusText.includes('Trial')) return 'info';
    if (statusText.includes('Required') || statusText.includes('Expired')) return 'error';
    if (statusText.includes('Unclaimed')) return 'default';
    return 'warning';
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Gateway Management
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between" 
        alignItems={isMobile ? 'stretch' : 'center'} 
        mb={3}
        gap={2}
      >
        <Typography variant="h4">
          Gateway Management
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 2,
          width: isMobile ? '100%' : 'auto'
        }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchGateways}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<QrCodeIcon />}
            onClick={() => handleDialogOpen('claim')}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
          >
            Claim Gateway
          </Button>
        </Box>
      </Box>

      {/* Subscription Status Banner */}
      {subscriptionInfo && (subscriptionInfo.inTrial || subscriptionInfo.expired) && (
        <Alert 
          severity={subscriptionInfo.expired ? "error" : "info"} 
          sx={{ mb: 3 }}
          icon={<SubscriptionIcon />}
          action={
            <Button 
              color="inherit" 
              size="small" 
              variant="outlined" 
              onClick={() => navigate('/subscription/plans')}
              startIcon={<SubscriptionIcon />}
            >
              {subscriptionInfo.expired ? 'Subscribe Now' : 'Subscribe Early'}
            </Button>
          }
        >
          <Box>
            <Typography variant="body1" fontWeight="bold">
              {subscriptionInfo.expired ? 'Trial Expired - Subscription Required' : subscriptionInfo.status}
            </Typography>
            <Typography variant="body2">
              {subscriptionInfo.expired 
                ? 'Your gateways cannot connect until you subscribe. Only $15/month per gateway.'
                : 'Subscribe early and secure your service. Only $15/month per gateway.'}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Gateway Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Gateways
              </Typography>
              <Typography variant="h5">
                {gateways.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Online
              </Typography>
              <Typography variant="h5" color="success.main">
                {gateways.filter(g => g.status === 'online').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Offline
              </Typography>
              <Typography variant="h5" color="error.main">
                {gateways.filter(g => g.status === 'offline').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Warnings
              </Typography>
              <Typography variant="h5" color="warning.main">
                {gateways.filter(g => g.status === 'warning').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Toggle Switch for Admin View - Only visible to admins */}
      {isAdmin && (
        <FormControlLabel
          control={
            <Switch
              checked={showAllGateways}
              onChange={(e) => {
                const newValue = e.target.checked;
                setShowAllGateways(newValue);
                // Pass the new value directly since state updates are async
                fetchGatewaysWithFilter(newValue);
              }}
              name="showAllGateways"
              color="primary"
            />
          }
          label="Show All Gateways (including claimed by others)"
          sx={{ mb: 2 }}
        />
      )}

      {/* Gateway Table (Desktop) / Cards (Mobile) */}
      {isMobile ? (
        // Mobile Card View
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {gateways.map((gateway) => (
            <Card key={gateway.id} sx={{ width: '100%' }}>
              <CardContent>
                {/* Status & Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    {getStatusIcon(gateway.status)}
                    <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight="bold" noWrap>
                        {gateway.name}
                      </Typography>
                      <Chip
                        label={gateway.status}
                        color={getStatusColor(gateway.status)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                  <IconButton
                    onClick={(e) => handleMenuClick(e, gateway)}
                    sx={{ ml: 1 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                {/* Gateway Details */}
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Location</Typography>
                    <Typography variant="body2">{gateway.location || 'Unknown'}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">Last Seen</Typography>
                    <Typography variant="body2">{formatLastSeen(gateway.lastSeen)}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">Subscription</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={getSubscriptionStatus(gateway)}
                        color={getSubscriptionStatusColor(getSubscriptionStatus(gateway))}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  {isAdmin && gateway.owner && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">Owner</Typography>
                      <Typography variant="body2">{gateway.owner.username}</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>

              {/* Action Button */}
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<LaunchIcon />}
                  onClick={() => handleGatewayAccess(gateway)}
                  color="primary"
                >
                  Connect to Gateway
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop Table View
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Gateway Name</TableCell>
                  <TableCell>Device Token</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Connected Users</TableCell>
                  <TableCell>Subscription</TableCell>
                  {isAdmin && <TableCell>Owner</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gateways.map((gateway) => (
                  <TableRow key={gateway.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Tooltip title={gateway.status}>
                          {getStatusIcon(gateway.status)}
                        </Tooltip>
                        <Chip
                          label={gateway.status}
                          color={getStatusColor(gateway.status)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <RouterIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {gateway.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {gateway.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Click to copy device token">
                        <Box
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            p: 0.5,
                            borderRadius: 1,
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => {
                            navigator.clipboard.writeText(gateway.deviceToken);
                            // Optional: show a brief toast/snackbar notification
                          }}
                        >
                          {gateway.deviceToken}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{gateway.location || 'Unknown'}</TableCell>
                    <TableCell>{gateway.publicIp}</TableCell>
                    <TableCell>{gateway.version}</TableCell>
                    <TableCell>{formatLastSeen(gateway.lastSeen)}</TableCell>
                    <TableCell>
                      <Chip
                        label={gateway.connectedUsers || 0}
                        color={gateway.connectedUsers > 0 ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getSubscriptionStatus(gateway)}
                        color={getSubscriptionStatusColor(getSubscriptionStatus(gateway))}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {gateway.owner ? (
                          <Typography variant="body2" color="textSecondary">
                            {gateway.owner.username}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="error">
                            Unclaimed
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<LaunchIcon />}
                          onClick={() => handleGatewayAccess(gateway)}
                          color="primary"
                          sx={{ minWidth: 'auto' }}
                        >
                          Connect
                        </Button>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, gateway)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* Show "Claim" option only for unclaimed gateways */}
        {menuGateway && !menuGateway.owner && (
          <MenuItem onClick={() => handleAdminClaimGateway(menuGateway)}>
            <PersonAddIcon sx={{ mr: 1 }} />
            Claim Gateway
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDialogOpen('edit', menuGateway)}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Gateway
        </MenuItem>
        <MenuItem onClick={() => handleDialogOpen('vpn', menuGateway)}>
          <VpnKeyIcon sx={{ mr: 1 }} />
          Connection Details
        </MenuItem>
        {menuGateway && GATEWAY_SERVICES
          .filter((svc) => !svc.adminOnly || isAdmin)
          .map((svc) => (
            <MenuItem
              key={svc.key}
              onClick={() => handleGatewayAccess(menuGateway, svc.path)}
              disabled={menuGateway.status !== 'online'}
            >
              <LaunchIcon sx={{ mr: 1 }} />
              {svc.label}
            </MenuItem>
          ))}
        <MenuItem onClick={() => handleDialogOpen('delete', menuGateway)}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Gateway
        </MenuItem>
      </Menu>

      {/* Dialogs for Edit/Delete/Connection Details/Claim */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'edit' && 'Edit Gateway'}
          {dialogType === 'delete' && 'Delete Gateway'}
          {dialogType === 'vpn' && 'Connection Details'}
          {dialogType === 'claim' && 'Claim Gateway'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'edit' && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Gateway Name"
                value={editGatewayName}
                onChange={(e) => setEditGatewayName(e.target.value)}
                margin="normal"
                helperText="Give your gateway a friendly name (e.g., Front Gate, Main Entrance)"
              />
              <TextField
                fullWidth
                label="Location"
                value={editGatewayLocation}
                onChange={(e) => setEditGatewayLocation(e.target.value)}
                margin="normal"
                helperText="Where is this gateway installed? (e.g., North Field, Barn 2)"
              />
            </Box>
          )}
          {dialogType === 'delete' && (
            <Typography>
              Are you sure you want to delete gateway "{selectedGateway?.name}"? 
              This action cannot be undone.
            </Typography>
          )}
          {dialogType === 'vpn' && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedGateway?.name}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">VPN INTERNAL IP</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {selectedGateway?.vpnInternalIp || 'Not assigned'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">DEVICE TOKEN</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {selectedGateway?.deviceToken || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">FIRMWARE VERSION</Typography>
                <Typography variant="body1">
                  {selectedGateway?.version || 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">LAST SEEN</Typography>
                <Typography variant="body1">
                  {selectedGateway?.lastSeen ? new Date(selectedGateway.lastSeen).toLocaleString() : 'Never'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">REGISTERED</Typography>
                <Typography variant="body1">
                  {selectedGateway?.createdAt ? new Date(selectedGateway.createdAt).toLocaleDateString() : 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">CONNECTION STATUS</Typography>
                <Chip 
                  label={selectedGateway?.status?.toUpperCase() || 'UNKNOWN'} 
                  color={selectedGateway?.status === 'online' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </Box>
          )}
          {dialogType === 'claim' && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Enter the device token from your gateway. You can find this by visiting your gateway's web interface at:
              </Typography>
              <Typography variant="body2" sx={{ 
                fontFamily: 'monospace', 
                backgroundColor: '#f5f5f5', 
                padding: 1, 
                borderRadius: 1,
                mb: 2 
              }}>
                http://your-gateway-ip/api/device-info
              </Typography>
              <Typography variant="body2" gutterBottom>
                Or scan the QR code on the device packaging/label.
              </Typography>
              <TextField
                fullWidth
                label="Device Token"
                placeholder="DT-A1B2C3D4E5F6"
                value={claimToken}
                onChange={(e) => setClaimToken(e.target.value)}
                margin="normal"
                helperText="Example: DT-A1B2C3D4E5F6"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          {dialogType !== 'vpn' && (
            <Button 
              variant="contained" 
              color={dialogType === 'delete' ? 'error' : 'primary'}
              onClick={() => {
                if (dialogType === 'claim') {
                  handleClaimGateway();
                } else if (dialogType === 'delete') {
                  handleDeleteGateway();
                } else if (dialogType === 'edit') {
                  handleEditGateway();
                }
              }}
              disabled={
                (dialogType === 'claim' && !claimToken.trim()) ||
                (dialogType === 'edit' && (!editGatewayName.trim() || !editGatewayLocation.trim()))
              }
            >
              {dialogType === 'edit' && 'Save Changes'}
              {dialogType === 'delete' && 'Delete'}
              {dialogType === 'claim' && 'Claim Gateway'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <SubscriptionPromptDialog
        open={subscriptionPromptOpen}
        gatewayName={blockedGateway?.name}
        onClose={() => setSubscriptionPromptOpen(false)}
      />
    </Box>
  );
};

export default Gateways;
